import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Alert,
} from "@mui/material";
import { Comment, Dataset, Notebook } from "../types";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import python from "react-syntax-highlighter/dist/esm/languages/hljs/python";
import bash from "react-syntax-highlighter/dist/esm/languages/hljs/bash";
import javascript from "react-syntax-highlighter/dist/esm/languages/hljs/javascript";
import typescript from "react-syntax-highlighter/dist/esm/languages/hljs/typescript";
import sql from "react-syntax-highlighter/dist/esm/languages/hljs/sql";
import {
  getFirstComment,
  getNextComment,
  getPrevComment,
} from "../utils/commentUtils";
import markdownComponents from "./MarkdownComponents";
import { CommentCard } from "./CommentCard";
import { cellBaseStyles, markdownStyles } from "../utils/markdownStyles";
import ReactMarkdown from "react-markdown";
import { getCodeLineStyle } from "./CodeLine";

// Регистрируем языки для подсветки синтаксиса
SyntaxHighlighter.registerLanguage("python", python);
SyntaxHighlighter.registerLanguage("bash", bash);
SyntaxHighlighter.registerLanguage("javascript", javascript);
SyntaxHighlighter.registerLanguage("js", javascript);
SyntaxHighlighter.registerLanguage("typescript", typescript);
SyntaxHighlighter.registerLanguage("ts", typescript);
SyntaxHighlighter.registerLanguage("sql", sql);

interface NotebookViewerProps {
  notebook: Notebook;
  comments: Comment[];
  onEditComment?: (commentId: number, text: string) => Promise<Comment | null>;
  onDeleteComment?: (commentId: number) => void;
  onCreateComment?: (comment: Comment) => Promise<Comment | null>;
  isReadOnly: boolean;
  dataset?: Dataset | null;
  commentDatasets?: Record<number, boolean>;
  addCommentToDataset?: (commentId: number) => void;
  removeCommentFromDataset?: (commentId: number) => void;
}

export const NotebookViewer: React.FC<NotebookViewerProps> = ({
  notebook,
  comments,
  onCreateComment,
  onEditComment,
  onDeleteComment,
  addCommentToDataset,
  removeCommentFromDataset,
  isReadOnly,
  commentDatasets,
  dataset,
}) => {
  const [tempComment, setTempComment] = useState<Comment | null>(null);
  const [activeComment, _setActiveComment] = useState<Comment | null>(null);
  const commentsRef = useRef(comments);
  const activeCommentRef = useRef(activeComment);
  const setActiveComment = (data: Comment | null) => {
    activeCommentRef.current = data;
    _setActiveComment(data);
  };

  useEffect(() => {
    commentsRef.current = comments;
  }, [comments]);

  const handleEditComment = async (commentId: number, text: string) => {
    if (!onEditComment) return;
    const updatedComment = await onEditComment(commentId, text);
    if (activeComment && activeComment.id === commentId) {
      setActiveComment(updatedComment);
    }
  };

  const handleCreateComment = async (text: string) => {
    if (!onCreateComment) return;
    const commentToCreate = {
      ...tempComment,
      text: text,
    } as Comment;
    const newComment = await onCreateComment(commentToCreate);

    setActiveComment(newComment);
    setTempComment(null);
  };

  const handleAddComment = (cellId: string, lineNumber: number) => {
    if (isReadOnly) return;
    const highlightedText = window.getSelection()?.toString() || "";
    if (highlightedText.length > 0) return;

    const tempComment: Comment = {
      id: -Date.now(),
      cellId,
      lineNumber,
      text: "",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      notebookId: notebook.id,
    };

    setActiveComment(tempComment);

    setTempComment(tempComment);
  };

  const handleDeleteComment = (commentId: number) => {
    if (!onDeleteComment) return;

    onDeleteComment(commentId);
    setActiveComment(null);
  };

  const syntaxHighlighterRenderer = (props: rendererProps) => {
    const { rows, stylesheet } = props;

    return rows.map((row, index) => {
      const cellId = row.properties?.["data-cell-id"];
      const lineNumber = row.properties?.["data-line-number"];
      const rowComment = comments.find(
        (c) => c.lineNumber === lineNumber && c.cellId === cellId
      );
      const hasTempComment =
        tempComment &&
        tempComment.cellId === cellId &&
        tempComment.lineNumber === lineNumber &&
        !isReadOnly;

      return (
        <div key={index}>
          {renderCodeRow(row, index, stylesheet)}
          {rowComment && (
            <CommentCard
              comment={rowComment}
              onClose={() => {
                if (isReadOnly) return;
                setActiveComment(null);
                if (activeComment && activeComment.id < 0) {
                  setTempComment(null);
                }
              }}
              onEdit={(commentId, text) => {
                if (isReadOnly) return;
                handleEditComment(parseInt(commentId), text);
              }}
              onDelete={(commentId) => {
                if (isReadOnly) return;
                handleDeleteComment(parseInt(commentId));
              }}
              onPrev={() => {
                setActiveComment(rowComment);
                goToPrevComment();
              }}
              onNext={() => {
                setActiveComment(rowComment);
                goToNextComment();
              }}
              showNavigation={commentsRef.current.length > 1}
              isReadOnly={isReadOnly}
              markdownComponents={markdownComponents}
              dataset={dataset}
              onAddToDataset={addCommentToDataset}
              onRemoveFromDataset={removeCommentFromDataset}
              isInDataset={commentDatasets?.[rowComment.id] || false}
            />
          )}
          {hasTempComment && (
            <CommentCard
              comment={tempComment}
              onClose={() => {
                setTempComment(null);
                setActiveComment(null);
              }}
              onEdit={(_commentId, text) => handleCreateComment(text)}
              onDelete={(commentId) => handleDeleteComment(parseInt(commentId))}
              onPrev={() => {}}
              onNext={() => {}}
              showNavigation={commentsRef.current.length > 1}
              initialEditMode={true}
              dataset={dataset}
              onAddToDataset={addCommentToDataset}
              onRemoveFromDataset={removeCommentFromDataset}
              isInDataset={false}
            />
          )}
        </div>
      );
    });
  };

  const renderCodeRow = (
    row: rendererNode,
    key: number,
    stylesheet?: any
  ): React.ReactNode => {
    if (row.type === "text") return row.value;

    if (row.type === "element") {
      const { tagName, properties, children } = row;
      const Tag = tagName as keyof JSX.IntrinsicElements;
      // Apply styles from stylesheet if className is present
      let elementProps = { ...properties };
      if (properties && properties.className && stylesheet) {
        const classNames = String(properties.className).split(" ");
        const stylesFromClass: any = {};

        classNames.forEach((className: string) => {
          if (stylesheet[className]) {
            Object.assign(stylesFromClass, stylesheet[className]);
          }
        });

        if (Object.keys(stylesFromClass).length > 0) {
          elementProps = {
            ...properties,
            style: {
              ...stylesFromClass,
              ...properties.style,
            },
          };
        }
      }

      const mappedChildren = children
        ? children.map((child, idx) => renderCodeRow(child, idx, stylesheet))
        : null;

      return React.createElement(Tag, { ...elementProps, key }, mappedChildren);
    }

    return null;
  };

  // Функция для прокрутки к комментарию
  const scrollToComment = (comment: Comment) => {
    if (!comment) return;

    setActiveComment(comment);

    const commentDOM = document.querySelector(
      `[data-cell-id="${comment.cellId}"][data-line-number="${comment.lineNumber}"]`
    );
    if (commentDOM) {
      commentDOM.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // Функция для перехода к следующему комментарию
  const goToNextComment = () => {
    if (commentsRef.current.length <= 1 || !activeCommentRef.current) return;

    const nextComment = getNextComment(
      commentsRef.current,
      activeCommentRef.current
    );
    if (nextComment) {
      setActiveComment(nextComment);
      scrollToComment(nextComment);
    }
  };

  const goToPrevComment = () => {
    if (commentsRef.current.length <= 1 || !activeCommentRef.current) return;

    const prevComment = getPrevComment(
      commentsRef.current,
      activeCommentRef.current
    );
    if (prevComment) {
      scrollToComment(prevComment);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (commentsRef.current.length === 0) return;

    const usedNavigationButton = [
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
    ].includes(e.key);
    if (!usedNavigationButton) return;

    if (!activeCommentRef.current && commentsRef.current.length > 0) {
      const firstComment = getFirstComment(commentsRef.current);
      if (firstComment) {
        scrollToComment(firstComment);
      }
      return;
    }
    if (activeCommentRef.current && commentsRef.current.length > 1) {
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        goToPrevComment();
      } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        goToNextComment();
      }
    }
  };

  const renderCell = (cell: any, index: number) => {
    const cellId = `cell-${index}`;
    const cellComments = commentsRef.current.filter(
      (c: Comment) => c.cellId === cellId
    );

    const cellText = Array.isArray(cell.source)
      ? cell.source.join("")
      : String(cell.source || "");
    const isMarkdownCell = cell.cell_type === "markdown";

    const renderContent = () => {
      if (isMarkdownCell) {
        const isCellSelected = activeComment?.cellId === cellId;

        return (
          <Paper
            elevation={0}
            sx={{
              ...(isCellSelected && { borderLeft: "4px solid #2196F3" }),
              ...(cellComments.length > 0 &&
                !isCellSelected && {
                  borderLeft: "4px solid rgb(14, 27, 55)",
                }),

              backgroundColor: isCellSelected
                ? "rgba(33, 150, 243, 0.1)"
                : "#FFFFFF",
              ...cellBaseStyles,
            }}
            onClick={() => {
              if (!isReadOnly) {
                const hasComments = cellComments.some(
                  (c) => c.lineNumber === 0
                );
                if (hasComments) {
                  const comment = cellComments.find((c) => c.lineNumber === 0);
                  if (comment) {
                    setActiveComment(
                      activeComment?.id === comment.id ? null : comment
                    );
                    if (comment && activeComment?.id !== comment.id) {
                      scrollToComment(comment);
                    }
                  }
                } else {
                  handleAddComment(cellId, 0);
                }
              }
            }}
            data-line-number="0"
            data-cell-id={cellId}
          >
            <ReactMarkdown components={markdownComponents}>
              {cellText}
            </ReactMarkdown>
          </Paper>
        );
      }

      return (
        <div className="code-cell-container" style={{ position: "relative" }}>
          {/* Основной код */}
          <div className="code-content">
            <SyntaxHighlighter
              language="python"
              style={markdownStyles}
              wrapLines={true}
              wrapLongLines={true}
              showLineNumbers={true}
              showInlineLineNumbers={true}
              lineNumberStyle={{ color: "#808080", fontStyle: "italic" }}
              renderer={syntaxHighlighterRenderer}
              customStyle={{
                margin: 0,
                wordBreak: "break-word",
                fontSize: "14px",
                borderRadius: "4px",
                padding: "12px",
                backgroundColor: "#FFFFFF",
              }}
              lineProps={(lineNumber: number) => {
                const comment = cellComments.find(
                  (c) => c.lineNumber === lineNumber - 1
                );

                const isSelected =
                  activeComment &&
                  activeComment.lineNumber === lineNumber - 1 &&
                  activeComment.cellId === cellId;

                const style = getCodeLineStyle(!!comment, isSelected, false);

                return {
                  // hover styles are handled in NotebookPage.css file
                  style,
                  onClick: () => {
                    // Если есть комментарии, показываем первый из них
                    if (comment) {
                      setActiveComment(
                        activeComment?.id === comment.id ? null : comment
                      );
                      if (comment && activeComment?.id !== comment.id) {
                        scrollToComment(comment);
                      }
                      return;
                    }

                    handleAddComment(cellId, lineNumber - 1);
                  },
                  "data-line-number": lineNumber - 1,
                  "data-cell-id": cellId,
                };
              }}
            >
              {cellText}
            </SyntaxHighlighter>
          </div>
        </div>
      );
    };

    return (
      <Card key={cellId} sx={{ mb: 2, position: "relative" }}>
        <CardContent sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ flex: 1, position: "relative" }}>
            <Box data-cell-id={cellId}>{renderContent()}</Box>

            {/* Вывод ячейки */}
            {cell.outputs &&
              cell.outputs.map((output: any, outputIndex: number) => (
                <Box key={outputIndex} sx={{ mt: 1, mb: 1 }}>
                  {output.output_type === "stream" && (
                    <SyntaxHighlighter
                      language="plaintext"
                      style={markdownStyles}
                      wrapLines={true}
                      wrapLongLines={true}
                      customStyle={{
                        margin: 0,
                        padding: "8px 12px",
                        backgroundColor: "#F8F9FA",
                        borderRadius: "4px",
                        fontSize: "14px",
                        border: "1px solid #E0E0E0",
                      }}
                    >
                      {Array.isArray(output.text)
                        ? output.text.join("")
                        : output.text}
                    </SyntaxHighlighter>
                  )}
                  {output.output_type === "execute_result" && (
                    <SyntaxHighlighter
                      language="plaintext"
                      style={markdownStyles}
                      wrapLines={true}
                      wrapLongLines={true}
                      customStyle={{
                        margin: 0,
                        padding: "8px 12px",
                        backgroundColor: "#F8F9FA",
                        borderRadius: "4px",
                        fontSize: "14px",
                        border: "1px solid #E0E0E0",
                      }}
                    >
                      {Array.isArray(output.data["text/plain"])
                        ? output.data["text/plain"].join("")
                        : output.data["text/plain"]}
                    </SyntaxHighlighter>
                  )}
                  {output.output_type === "display_data" &&
                    output.data["image/png"] && (
                      <img
                        src={`data:image/png;base64, ${output.data["image/png"]}`}
                        alt="Output"
                        style={{
                          maxHeight: "600px",
                          width: "auto",
                          maxWidth: "100%",
                          borderRadius: "4px",
                        }}
                      />
                    )}
                </Box>
              ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>
        {notebook.title}
      </Typography>

      {notebook.comment && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: "#f5f5f5" }}>
          <Typography variant="body1">{notebook.comment}</Typography>
        </Paper>
      )}

      {(() => {
        try {
          if (!notebook.content) {
            return (
              <Alert severity="error" sx={{ mt: 2 }}>
                Содержимое ноутбука отсутствует
              </Alert>
            );
          }

          const notebookData =
            typeof notebook.content === "string"
              ? JSON.parse(notebook.content)
              : notebook.content;

          if (
            !notebookData ||
            !notebookData.cells ||
            !Array.isArray(notebookData.cells)
          ) {
            return (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Некорректная структура ноутбука: отсутствуют ячейки или они не в
                формате массива
              </Alert>
            );
          }

          return notebookData.cells.map((cell: any, index: number) =>
            renderCell(cell, index)
          );
        } catch (error) {
          return (
            <Alert severity="error" sx={{ mt: 2 }}>
              Ошибка при обработке содержимого ноутбука: {String(error)}
            </Alert>
          );
        }
      })()}
    </>
  );
};
